import React, { useEffect, useState } from "react"
import {
	Box,
	Button,
	HStack,
	Image,
	Icon,
	Text,
	Tag,
	TagLabel,
	useToast,
	Grid,
	GridItem,
	Flex,
	Spinner,
	useMediaQuery,
} from "@chakra-ui/react"
import { AttachmentIcon } from "@chakra-ui/icons"
import {
	BsHouseDoor,
	BsFileEarmarkText,
	BsCalendarDate,
	BsCheckCircle,
} from "react-icons/bs"
import { dateToFormat, convertToAMPM } from "../Utilities"
import { Partners } from "../partnershipData"
import { collection, query, where, getDocs } from "firebase/firestore"
import { db } from "../firebase"

export const JobDetailsInformationBox = ({ jobRequest, userPlus }) => {
	const partner = Partners.find((p) => p.id === userPlus?.partner)
	const toast = useToast()
	const [invoices, setInvoices] = useState([])
	const [completedPhotos, setCompletedPhotos] = useState([])
	const [loading, setLoading] = useState({ invoices: false, photos: false })

	// Use direct media query instead of useBreakpointValue
	const [isLargerThanMd] = useMediaQuery("(min-width: 48em)")

	// Set a default state for the view on initial render
	const [isDesktop, setIsDesktop] = useState(false)

	// Update the state once we've determined the breakpoint
	useEffect(() => {
		setIsDesktop(isLargerThanMd)
	}, [isLargerThanMd])

	// Fetch invoices and completed photos from the database
	useEffect(() => {
		if (!jobRequest?.id) return

		const fetchInvoices = async () => {
			setLoading((prev) => ({ ...prev, invoices: true }))
			try {
				const q = query(
					collection(db, "jobRequestInvoices"),
					where("jobRequestId", "==", jobRequest.id)
				)
				const querySnapshot = await getDocs(q)
				const invoiceData = querySnapshot.docs.map((doc) => doc.data())
				setInvoices(invoiceData)
			} catch (error) {
				console.error("Error fetching invoices:", error)
				toast({
					title: "Failed to load invoices",
					status: "error",
					duration: 3000,
					isClosable: true,
				})
			} finally {
				setLoading((prev) => ({ ...prev, invoices: false }))
			}
		}

		const fetchCompletedPhotos = async () => {
			setLoading((prev) => ({ ...prev, photos: true }))
			try {
				const q = query(
					collection(db, "jobRequestCompletion"),
					where("jobRequestId", "==", jobRequest.id)
				)
				const querySnapshot = await getDocs(q)
				const photosData = querySnapshot.docs.map((doc) => doc.data())

				const allPhotoFiles = photosData.reduce((allFiles, currentDoc) => {
					if (
						currentDoc.completionFiles &&
						Array.isArray(currentDoc.completionFiles)
					) {
						return [...allFiles, ...currentDoc.completionFiles]
					}
					return allFiles
				}, [])

				// Set the consolidated result
				setCompletedPhotos(allPhotoFiles)
			} catch (error) {
				console.error("Error fetching completed photos:", error)
				toast({
					title: "Failed to load completion photos",
					status: "error",
					duration: 3000,
					isClosable: true,
				})
			} finally {
				setLoading((prev) => ({ ...prev, photos: false }))
			}
		}

		fetchInvoices()
		fetchCompletedPhotos()
	}, [jobRequest?.id, toast])

	const handleJobRequestDocuments = () => {
		jobRequest.files.forEach((file) => {
			window.open(file, "_blank")
		})
		return toast({
			title:
				"If you're unable to view attachments, disable your pop-up blocker.",
			status: "loading",
			duration: 6000,
			isClosable: true,
		})
	}

	const handleViewInvoice = () => {
		if (invoices.length > 0 && invoices[0].invoiceFiles) {
			invoices[0].invoiceFiles.forEach((file) => {
				window.open(file, "_blank")
			})
			return toast({
				title:
					"If you're unable to view invoices, disable your pop-up blocker.",
				status: "loading",
				duration: 6000,
				isClosable: true,
			})
		}
	}

	const handleViewPhotos = () => {
		if (completedPhotos.length > 0) {
			completedPhotos.forEach((file) => {
				window.open(file, "_blank")
			})
			return toast({
				title: "If you're unable to view photos, disable your pop-up blocker.",
				status: "loading",
				duration: 6000,
				isClosable: true,
			})
		}
	}

	// Original mobile layout
	if (!isDesktop) {
		return (
			<Box
				bgColor="white"
				boxShadow="md"
				borderRadius="md"
				display="flex"
				position="relative"
			>
				<Image
					src={
						partner
							? `/assets/${partner.id}_house_banner_icon.svg`
							: "/assets/house_banner_icon.svg"
					}
					position={{ md: "absolute" }}
					display={{ base: "none", lg: "flex" }}
				/>
				<Image
					src={
						partner
							? `/assets/${partner.id}_house_banner_icon2.svg`
							: "/assets/house_banner_icon2.svg"
					}
					position="absolute"
					top="0"
					right="0"
					display={{ base: "flex", lg: "none" }}
				/>
				<Box
					display={{ lg: "flex" }}
					width={{ base: "100%", lg: "80%" }}
					justifyContent="space-between"
					p="20px"
					ml={{ lg: "125px" }}
					mt={{ base: "25px", lg: "0" }}
				>
					<Box ml={{ lg: "10px" }}>
						<HStack alignItems={{ base: "center", lg: "start" }}>
							<Image src="/assets/user_group.svg" />
							<Text fontWeight="semibold" fontSize="lg">
								Client Info
							</Text>
						</HStack>
						<Box mt={{ base: "5px", lg: "15px" }}>
							<Text>{jobRequest?.locationAddress}</Text>
							{jobRequest?.clientEmailAddress &&
								jobRequest?.clientEmailAddress.map((client, index) => (
									<Tag
										key={index}
										borderRadius="full"
										variant="solid"
										bgColor="borderColor"
										mr="5px"
										mt="5px"
										mb="5px"
									>
										<TagLabel>{client}</TagLabel>
									</Tag>
								))}
						</Box>
					</Box>
					<Box ml={{ lg: "30px" }} mt={{ base: "10px", lg: "0px" }}>
						<HStack alignItems={{ base: "center", md: "start" }}>
							<Image src="/assets/truck.svg" />
							<Text fontWeight="semibold" fontSize="lg">
								Preferred Dates & Times
							</Text>
						</HStack>
						<Box mt={{ base: "5px", lg: "15px" }}>
							{jobRequest?.servicePreferredDates?.map((entry, index) => (
								<HStack gap={5} mb="5px" key={index}>
									<Text key={index}>
										{dateToFormat(entry.date)} {convertToAMPM(entry.startTime)}{" "}
										- {convertToAMPM(entry.endTime)}
									</Text>
								</HStack>
							))}
						</Box>
					</Box>
					<Box ml={{ lg: "100px" }} mt={{ base: "10px", lg: "0px" }}>
						<HStack alignItems={{ base: "center", md: "start" }}>
							<Image src="/assets/clipboard_list.svg" />
							<Text fontWeight="semibold" fontSize="lg">
								{jobRequest?.serviceType}{" "}
								{jobRequest?.subServiceType && (
									<Text fontSize="md">- {jobRequest?.subServiceType}</Text>
								)}
							</Text>
						</HStack>

						<Box mt={{ base: "5px", lg: "15px" }}>
							{jobRequest?.jobRequestDetails && (
								<Text>{jobRequest?.jobRequestDetails}</Text>
							)}
							{jobRequest?.files?.length > 0 && (
								<Button
									leftIcon={<Icon as={AttachmentIcon} />}
									bg="borderColor"
									color="white"
									textDecoration="none"
									borderRadius="full"
									_hover={{ bgColor: "borderColor" }}
									mt={2}
									onClick={() => handleJobRequestDocuments()}
								>
									View {jobRequest.files.length}{" "}
									{jobRequest.files.length > 1 ? "attachments" : "attachment"}
								</Button>
							)}

							{/* Add mobile buttons for invoices and photos */}
							{!loading.invoices &&
								invoices.length > 0 &&
								invoices[0].invoiceFiles && (
									<Button
										leftIcon={<AttachmentIcon />}
										bg="borderColor"
										color="white"
										textDecoration="none"
										borderRadius="full"
										_hover={{ bgColor: "borderColor" }}
										mt={2}
										onClick={handleViewInvoice}
									>
										View invoice
									</Button>
								)}

							{!loading.photos && completedPhotos.length > 0 && (
								<Button
									leftIcon={<Icon as={BsCheckCircle} />}
									bg="borderColor"
									color="white"
									textDecoration="none"
									borderRadius="full"
									_hover={{ bgColor: "borderColor" }}
									mt={2}
									onClick={handleViewPhotos}
								>
									View {completedPhotos.length} photos
								</Button>
							)}
						</Box>
					</Box>
				</Box>
			</Box>
		)
	}

	// New tablet/desktop layout with cards
	return (
		<Grid
			templateColumns={{ md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }}
			gap={6}
			width="100%"
		>
			{/* Property Info Card */}
			<GridItem>
				<Box
					bgColor="white"
					boxShadow="sm"
					borderRadius="md"
					p={5}
					height="100%"
				>
					<Flex alignItems="center" mb={3}>
						<Icon as={BsHouseDoor} mr={2} boxSize={5} />
						<Text fontWeight="bold" fontSize="md">
							PROPERTY
						</Text>
					</Flex>
					<Text fontSize="sm" mb={1}>
						{jobRequest?.locationAddress}
					</Text>
					<Box mt={2}>
						{jobRequest?.clientEmailAddress &&
							jobRequest?.clientEmailAddress.map((client, index) => (
								<Text key={index} fontSize="sm" color="gray.500">
									{client}
								</Text>
							))}
					</Box>
				</Box>
			</GridItem>

			{/* Job Request Details Card */}
			<GridItem>
				<Box
					bgColor="white"
					boxShadow="sm"
					borderRadius="md"
					p={5}
					height="100%"
					position="relative"
				>
					<Flex alignItems="center" mb={3}>
						<Icon as={BsFileEarmarkText} mr={2} boxSize={5} />
						<Text fontWeight="bold" fontSize="md">
							JOB REQUEST DETAILS
						</Text>
					</Flex>
					<Text fontSize="sm" fontWeight="medium" mb={1}>
						{jobRequest?.serviceType}
						{jobRequest?.subServiceType && ` & ${jobRequest?.subServiceType}`}
					</Text>
					<Text
						color="blue.500"
						fontSize="sm"
						cursor="pointer"
						_hover={{ textDecoration: "underline" }}
						mb={4}
						onClick={() => {
							toast({
								title: "Job Details",
								description:
									jobRequest?.jobRequestDetails || "No details provided",
								status: "info",
								duration: 6000,
								isClosable: true,
							})
						}}
					>
						View job details
					</Text>

					{loading.invoices ? (
						<Spinner size="sm" color="gray.400" />
					) : (
						jobRequest?.files?.length > 0 && (
							<Button
								leftIcon={<AttachmentIcon />}
								size="sm"
								variant="outline"
								borderColor="borderColor"
								textColor="borderColor"
								borderRadius="full"
								onClick={handleJobRequestDocuments}
								width="100%"
							>
								View {jobRequest.files.length}{" "}
								{jobRequest.files.length > 1 ? "attachments" : "attachment"}
							</Button>
						)
					)}
				</Box>
			</GridItem>

			{/* Preferred Dates Card */}
			<GridItem>
				<Box
					bgColor="white"
					boxShadow="sm"
					borderRadius="md"
					p={5}
					height="100%"
				>
					<Flex alignItems="center" mb={3}>
						<Icon as={BsCalendarDate} mr={2} boxSize={5} />
						<Text fontWeight="bold" fontSize="md">
							PREFERRED DATES
						</Text>
					</Flex>
					<Box>
						{jobRequest?.servicePreferredDates?.map((entry, index) => (
							<Flex key={index} direction="column" mb={2}>
								<Text fontSize="sm">{dateToFormat(entry.date)}</Text>
								<Text fontSize="sm" color="gray.500">
									{convertToAMPM(entry.startTime)} -{" "}
									{convertToAMPM(entry.endTime)}
								</Text>
							</Flex>
						))}
					</Box>
				</Box>
			</GridItem>

			{/* Completed Job Notes Card */}
			{(completedPhotos.length > 0 || invoices.length > 0) && (
				<GridItem>
					<Box
						bgColor="white"
						boxShadow="sm"
						borderRadius="md"
						p={5}
						height="100%"
					>
						<Flex alignItems="center" mb={3}>
							<Icon as={BsCheckCircle} mr={2} boxSize={5} />
							<Text fontWeight="bold" fontSize="md">
								COMPLETED JOB NOTES
							</Text>
						</Flex>

						<Flex direction="column" gap={2}>
							{loading.photos ? (
								<Spinner size="sm" color="gray.400" mb={2} />
							) : (
								completedPhotos.length > 0 && (
									<Button
										leftIcon={<Icon as={BsCheckCircle} />}
										size="sm"
										borderColor="borderColor"
										textColor="borderColor"
										variant="outline"
										borderRadius="full"
										onClick={handleViewPhotos}
										width="100%"
									>
										View {completedPhotos.length} photos
									</Button>
								)
							)}

							{loading.invoices ? (
								<Spinner size="sm" color="gray.400" />
							) : (
								invoices.length > 0 &&
								invoices[0].invoiceFiles && (
									<Button
										leftIcon={<AttachmentIcon />}
										size="sm"
										borderColor="borderColor"
										textColor="borderColor"
										variant="outline"
										borderRadius="full"
										onClick={handleViewInvoice}
										width="100%"
									>
										View invoice
									</Button>
								)
							)}
						</Flex>
					</Box>
				</GridItem>
			)}
		</Grid>
	)
}
